import { html, TemplateResult } from "lit";
import { customElement, property } from "lit/decorators.js";
import { MachineAnnotation } from "@/state/MachineAnnotations";
import { DodonaElement } from "@/components/meta/dodona_element";
import { I18n } from "@/i18n/i18n";
import { unsafeHTML } from "lit/directives/unsafe-html.js";
import { renderMarkdown } from "@/feedback/markdown";

/**
 * This component represents a machine annotation.
 *
 * @element d-machine-annotation
 *
 * @prop {MachineAnnotation} data - The machine annotation data.
 */
@customElement("d-machine-annotation")
export class MachineAnnotationComponent extends DodonaElement {
    @property({ type: Object })
    data: MachineAnnotation;

    protected get hasNotice(): boolean {
        return this.data.externalUrl !== null && this.data.externalUrl !== undefined;
    }

    protected get isMarkdown(): boolean {
        return this.data.format === "markdown";
    }

    protected get text(): string {
        // Filter out lines only containing dashes.
        return this.data.text
            .split("\n")
            .filter((s) => !/^--*$/.test(s))
            .join("\n");
    }

    protected get content(): string | ReturnType<typeof unsafeHTML> {
        return this.isMarkdown ? unsafeHTML(renderMarkdown(this.data.text)) : this.text;
    }

    private onMouseEnter(): void {
        this.data.isHovered = true;
    }

    private onMouseLeave(): void {
        this.data.isHovered = false;
    }

    private onFocusIn(): void {
        this.data.isHovered = true;
    }

    private onFocusOut(): void {
        this.data.isHovered = false;
    }

    render(): TemplateResult {
        // i18n-tasks-use t('js.annotation.type.error') t('js.annotation.type.info') t('js.annotation.type.warning')
        const typeLabel = I18n.t(`js.annotation.type.${this.data.type}`);
        const noticeLabel = I18n.t("js.machine_annotation.external_url");

        return html`
            <div
                class="annotation machine-annotation ${this.data.type}"
                @mouseenter=${this.onMouseEnter}
                @mouseleave=${this.onMouseLeave}
                @focusin=${this.onFocusIn}
                @focusout=${this.onFocusOut}
            >
                <div class="annotation-header">
                    <span class="annotation-meta">
                        ${typeLabel}
                        ${
                            this.hasNotice
                                ? html`
                                      <span>
                                          ·
                                          <a href=${this.data.externalUrl} target="_blank">${noticeLabel}</a>
                                      </span>
                                  `
                                : ""
                        }
                    </span>
                </div>
                <div class="annotation-text ${this.isMarkdown ? "annotation-markdown" : ""}">${this.content}</div>
            </div>
        `;
    }
}
